import * as React from 'react'
import { Unplug } from 'lucide-react'
import { Alert } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { displayDateTime } from '@/lib/dates'
import type {
  AccountingConnection,
  AccountingSettings,
  AccountingSyncHealth,
} from '@/pages/AccountingSettingsPage'

export function AccountingConnectionAudit({ settings }: { settings: AccountingSettings }) {
  const audit = settings.company_state_audit ?? []
  const history = settings.financial_history ?? []
  if (audit.length === 0 && history.length === 0) return null

  return (
    <section className="p-4">
      <h2 className="text-body font-semibold">Accounting Connection history</h2>
      <div className="space-y-3 pt-3 text-body-sm">
        {history.map((entry) => (
          <div
            key={entry.id}
            className="flex flex-wrap items-center gap-2 border-t border-border py-2 first:border-0"
          >
            <span>{entry.event}</span>
            <span className="text-caption text-muted-foreground">{entry.actor}</span>
          </div>
        ))}
        {audit.map((entry) => (
          <div
            key={`${entry.company_name}-${entry.archived_at}`}
            className="border-t border-border pt-2"
          >
            <p className="font-medium text-foreground">{entry.company_name}</p>
            <p className="text-caption text-muted-foreground">
              Archived: {entry.revenue_destination_mappings} Revenue Destination mapping(s),{' '}
              {entry.payment_deposit_account_mappings ?? 0} Payment deposit-account mapping(s),{' '}
              {entry.external_identities} external identity link(s)
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

export function AccountingConnectionNotice({
  connection,
}: {
  connection: AccountingConnection | null | undefined
}) {
  if (connection?.status !== 'disconnected') return null

  return (
    <Alert variant="warning" className="mb-4 max-w-2xl">
      QuickBooks Online is disconnected: {connection.disconnected_reason}. Reconnect it from
      Accounting settings. Association work continues while it is disconnected.
    </Alert>
  )
}

export function ConnectedCompany({
  connection,
  health,
  pending,
  onReconnect,
  onChangeCompany,
  disconnecting,
  onDisconnect,
  viewOnly = false,
}: {
  connection: AccountingConnection
  health?: AccountingSyncHealth
  pending: boolean
  onReconnect: () => void
  onChangeCompany: () => void
  disconnecting: boolean
  onDisconnect: () => void
  viewOnly?: boolean
}) {
  const disconnected = connection.status === 'disconnected'
  const [dialog, setDialog] = React.useState<'disconnect' | 'change-company' | null>(null)
  return (
    <div className="space-y-3 text-body-sm">
      <div className="flex items-center gap-2">
        <Badge variant={disconnected ? 'warning' : 'success'}>
          {disconnected ? 'Disconnected' : 'Connected'}
        </Badge>
        <span className="font-medium text-foreground">{connection.company_name}</span>
      </div>
      {disconnected && (
        <div className="space-y-2">
          <p className="text-muted-foreground">{connection.disconnected_reason}</p>
          <Button size="sm" disabled={pending} onClick={onReconnect}>
            {pending ? 'Opening QuickBooks...' : 'Reconnect QuickBooks Online'}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={pending}
            onClick={() => setDialog('change-company')}
          >
            Connect a different company
          </Button>
        </div>
      )}
      <dl className="grid gap-2 sm:grid-cols-2">
        <Detail label="Provider" value={connection.provider} />
        <Detail label="Connected" value={displayDateTime(connection.connected_at)} />
        {health && (
          <>
            <Detail
              label="Last successful push"
              value={
                health.last_successful_push_at
                  ? displayDateTime(health.last_successful_push_at)
                  : 'No successful push yet'
              }
            />
            <Detail label="Queue depth" value={health.queue_depth.toString()} />
            <div className="sm:col-span-2">
              <dt className="text-caption text-muted-foreground">Open tasks</dt>
              <dd className="text-foreground">
                {health.open_task_counts.needs_mapping} Needs mapping ·{' '}
                {health.open_task_counts.drift} Drift · {health.open_task_counts.error} Error
              </dd>
            </div>
          </>
        )}
      </dl>
      {!disconnected && !viewOnly && (
        <Button
          size="sm"
          variant="quietDestructive"
          disabled={disconnecting}
          onClick={() => setDialog('disconnect')}
        >
          <Unplug className="mr-2 h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
          Disconnect QuickBooks Online
        </Button>
      )}
      {!viewOnly && (
        <AccountingConnectionDialog
          mode={dialog}
          companyName={connection.company_name}
          pending={pending || disconnecting}
          onClose={() => setDialog(null)}
          onConfirm={() => {
            if (dialog === 'disconnect') onDisconnect()
            if (dialog === 'change-company') onChangeCompany()
            setDialog(null)
          }}
        />
      )}
    </div>
  )
}

function AccountingConnectionDialog({
  mode,
  companyName,
  pending,
  onClose,
  onConfirm,
}: {
  mode: 'disconnect' | 'change-company' | null
  companyName: string
  pending: boolean
  onClose: () => void
  onConfirm: () => void
}) {
  if (!mode) return null
  const changing = mode === 'change-company'
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {changing ? 'Connect a different company?' : 'Disconnect QuickBooks Online?'}
          </DialogTitle>
          <DialogDescription>
            {changing
              ? `Mappings and external identities for ${companyName} will be archived for audit. Queued Invoices will need new Revenue Destinations.`
              : 'Mappings, external identities, and queued Accounting work will be retained so reconnecting can resume safely.'}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant={changing ? 'default' : 'destructive'}
            disabled={pending}
            onClick={onConfirm}
          >
            {changing ? 'Continue to QuickBooks' : 'Disconnect'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function DisconnectedCompany({
  pending,
  onConnect,
}: {
  pending: boolean
  onConnect: () => void
}) {
  return (
    <div className="space-y-3 text-body-sm">
      <p className="font-medium text-foreground">No Accounting Provider connected</p>
      <Button size="sm" disabled={pending} onClick={onConnect}>
        {pending ? 'Opening QuickBooks...' : 'Connect QuickBooks Online'}
      </Button>
    </div>
  )
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-caption text-muted-foreground">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  )
}
